import { Package } from "lucide-react";
import { fmtKmRange } from "@/lib/wholesale-format";
import { cn } from "@/lib/utils";
import { Stepper } from "@/components/wholesale/stepper";
import type { CatalogRow, CatalogVariation, ItemSnapshot } from "@/services/wholesale-catalog";

/** Zaliha u riječima: proizvod koji ne vodi količinu ima samo status, pa se broj ne izmišlja. */
function stockLabel(qty: number | null | undefined, status?: string): string {
  if (qty === null || qty === undefined) return status === "instock" ? "ima" : "nema";
  return qty <= 0 ? "nema" : `${qty} kom`;
}

function isOut(it: { stockQuantity: number | null; stockStatus?: string }): boolean {
  return it.stockQuantity === null ? it.stockStatus !== "instock" : it.stockQuantity <= 0;
}

/** Zaliha na slici: broj komada mirno, nula crveno — kupac gleda ekran s pola metra
 *  i mora vidjeti razliku prije nego pročita brojku. */
function StockTag({ qty, status }: { qty: number | null | undefined; status?: string }) {
  const out = isOut({ stockQuantity: qty ?? null, stockStatus: status });
  return (
    <span
      className={cn(
        "absolute bottom-0 right-0 px-1.5 py-0.5 text-[10px] uppercase tracking-[0.08em] tabular-nums",
        out ? "bg-destructive text-white" : "bg-background/90 text-muted-foreground",
      )}
    >
      {stockLabel(qty, status)}
    </span>
  );
}

/** Jedan red brzog dodavanja: oznaka, zaliha, stepper. Rasprodana varijacija je zaključana —
 *  veleprodaja se dogovara na ono što stvarno stoji u skladištu. */
function QuickRow({
  label,
  stock,
  quantity,
  disabled,
  onChange,
}: {
  label: string;
  stock: string;
  quantity: number;
  disabled: boolean;
  onChange: (n: number) => void;
}) {
  return (
    <li className={cn("flex items-center gap-2 py-0.5 text-[11px]", disabled && "text-muted-foreground/50")}>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <span className="shrink-0 tabular-nums">{stock}</span>
      <Stepper size="sm" value={quantity} disabled={disabled} onChange={onChange} label={label} className="shrink-0" />
    </li>
  );
}

/** Jedna ploča izloga: uvijek krovni proizvod, nikad pojedinačna varijacija. Slika i naziv
 *  otvaraju detalj, a ispod cijene stoji lista varijacija sa svojim količinama i stepperom,
 *  pa količine idu u korpu bez otvaranja ijednog ekrana. */
export function ShowroomCard({
  item,
  unitPrice,
  unitPriceMax,
  discountPct,
  inCartQty,
  quantityOf,
  onSetQuantity,
  onOpen,
}: {
  item: CatalogRow;
  /** Cijena s rabatom — donja granica raspona. */
  unitPrice: number;
  /** Gornja granica raspona; jednaka donjoj kad su sve varijacije iste cijene. */
  unitPriceMax: number;
  discountPct: number;
  inCartQty: number;
  /** Količina te varijacije (ili samog proizvoda) koja je već u korpi. */
  quantityOf: (snap: ItemSnapshot) => number;
  onSetQuantity: (snap: ItemSnapshot, quantity: number) => void;
  onOpen: () => void;
}) {
  const discounted = discountPct > 0 && item.listPrice !== null;
  const variations: CatalogVariation[] = item.variations;
  const productOut = isOut(item);

  return (
    <div
      className={cn(
        // Rešetku crtaju same kartice, a ne razmak u mreži: prazne ćelije nepotpunog
        // zadnjeg reda inače propuste sivu podlogu i red izgleda kao da mu fali komad.
        "relative flex h-full min-w-0 flex-col overflow-hidden border-b border-r border-foreground/10 bg-background",
        inCartQty > 0 && "outline outline-2 -outline-offset-2 outline-foreground",
      )}
    >
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Detalji: ${item.name || "artikal"}`}
        className="relative aspect-square bg-muted transition-colors active:bg-muted/70 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-foreground"
      >
        {item.imageUrl ? (
          <img src={item.imageUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Package className="h-8 w-8 text-muted-foreground/40" />
          </div>
        )}
        {discounted && (
          <span className="absolute right-0 top-0 bg-background/90 px-1.5 py-0.5 text-[10px] uppercase tracking-[0.08em] tabular-nums">
            −{discountPct}%
          </span>
        )}
        {inCartQty > 0 && (
          <span className="absolute left-0 top-0 flex h-7 min-w-7 items-center justify-center bg-foreground px-1.5 text-sm font-semibold tabular-nums text-background">
            {inCartQty}
          </span>
        )}
        <StockTag qty={item.stockQuantity} status={item.stockStatus} />
      </button>

      <div className="flex min-w-0 flex-1 flex-col gap-1 px-2.5 py-2">
        <button
          type="button"
          onClick={onOpen}
          className="line-clamp-2 min-h-[2.5em] text-left text-[13px] leading-snug"
        >
          {item.name || "Bez naziva"}
        </button>
        <div className="flex min-w-0 items-baseline gap-x-2 overflow-hidden">
          <span className="shrink-0 whitespace-nowrap text-lg font-semibold tabular-nums leading-none">
            {fmtKmRange(unitPrice, unitPriceMax)}
          </span>
          {discounted && (
            <span className="truncate text-[11px] tabular-nums text-muted-foreground line-through">
              {fmtKmRange(item.listPrice, item.priceMax)}
            </span>
          )}
        </div>
        <span className="truncate text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
          {item.sku || "bez SKU"}
        </span>

        {variations.length > 0 ? (
          <ul className="flex flex-col border-t border-foreground/10 pt-1">
            {variations.map((v) => (
              <QuickRow
                key={v.wcVariationId ?? 0}
                label={v.label || v.sku || "—"}
                stock={stockLabel(v.stockQuantity, v.stockStatus)}
                quantity={quantityOf(v)}
                disabled={isOut(v)}
                onChange={(n) => onSetQuantity(v, n)}
              />
            ))}
          </ul>
        ) : (
          <div className="flex items-center justify-end border-t border-foreground/10 pt-1">
            <Stepper
              size="sm"
              value={quantityOf(item)}
              disabled={productOut}
              onChange={(n) => onSetQuantity(item, n)}
              label={item.name}
            />
          </div>
        )}
      </div>
    </div>
  );
}
